import React, { FunctionComponent, JSX, SVGProps } from "react";

type IconProps = SVGProps<SVGSVGElement> & { size?: number };

const Icon: FunctionComponent<IconProps & { children: JSX.Element }> = ({ size = 15, children, ...rest }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.5}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden={true}
    {...rest}>
    {children}
  </svg>
);

export const DashboardIcon: FunctionComponent<IconProps> = (props) => (
  <Icon {...props}>
    <>
      <rect x="2" y="2" width="5" height="5" rx="1" />
      <rect x="9" y="2" width="5" height="5" rx="1" />
      <rect x="2" y="9" width="5" height="5" rx="1" />
      <rect x="9" y="9" width="5" height="5" rx="1" />
    </>
  </Icon>
);

export const RegistrationsIcon: FunctionComponent<IconProps> = (props) => (
  <Icon {...props}>
    <path d="M2.5 4h11M2.5 8h11M2.5 12h7" />
  </Icon>
);

export const WaitlistIcon: FunctionComponent<IconProps> = (props) => (
  <Icon {...props}>
    <>
      <circle cx="8" cy="8" r="5.5" />
      <path d="M8 5v3l2 1.5" />
    </>
  </Icon>
);

export const OfferIcon: FunctionComponent<IconProps> = (props) => (
  <Icon {...props}>
    <>
      <rect x="2.5" y="2.5" width="11" height="11" rx="2" />
      <path d="M2.5 6.5h11" />
    </>
  </Icon>
);

export const FormIcon: FunctionComponent<IconProps> = (props) => (
  <Icon {...props}>
    <>
      <rect x="2.5" y="2.5" width="11" height="11" rx="2" />
      <path d="M5 6h6M5 9.5h4" />
    </>
  </Icon>
);

export const MailIcon: FunctionComponent<IconProps> = (props) => (
  <Icon {...props}>
    <>
      <rect x="2" y="3.5" width="12" height="9" rx="2" />
      <path d="M2.6 4.5 8 8.6l5.4-4.1" />
    </>
  </Icon>
);

export const AdminIcon: FunctionComponent<IconProps> = (props) => (
  <Icon {...props}>
    <>
      <circle cx="8" cy="5.5" r="2.4" />
      <path d="M3 13.2c.6-2.2 2.6-3.4 5-3.4s4.4 1.2 5 3.4" />
    </>
  </Icon>
);

export const UsersIcon: FunctionComponent<IconProps> = (props) => (
  <Icon {...props}>
    <>
      <circle cx="6" cy="5.5" r="2.2" />
      <path d="M1.8 13c.5-2 2.2-3.1 4.2-3.1s3.7 1.1 4.2 3.1" />
      <path d="M10.6 3.6a2.2 2.2 0 0 1 0 4.2M11.6 9.9c1.5.2 2.5 1.3 2.9 3.1" />
    </>
  </Icon>
);

export const SettingsIcon: FunctionComponent<IconProps> = (props) => (
  <Icon {...props}>
    <>
      <circle cx="8" cy="8" r="2.2" />
      <path d="M8 1.8v1.6M8 12.6v1.6M1.8 8h1.6M12.6 8h1.6" />
    </>
  </Icon>
);

export const CollapseIcon: FunctionComponent<IconProps> = (props) => (
  <Icon {...props}>
    <>
      <path d="M9.5 5 6 8l3.5 3" />
      <path d="M2.5 2.5v11" />
    </>
  </Icon>
);

export const ExpandIcon: FunctionComponent<IconProps> = (props) => (
  <Icon {...props}>
    <>
      <path d="M6.5 5 10 8l-3.5 3" />
      <path d="M13.5 2.5v11" />
    </>
  </Icon>
);

export const SignOutIcon: FunctionComponent<IconProps> = (props) => (
  <Icon {...props}>
    <>
      <path d="M6 13.5H3.5v-11H6" />
      <path d="M9 5.5 11.5 8 9 10.5M11.5 8h-6" />
    </>
  </Icon>
);

export const SearchIcon: FunctionComponent<IconProps> = ({ size = 13, ...rest }) => (
  <Icon size={size} strokeWidth={1.6} {...rest}>
    <>
      <circle cx="7" cy="7" r="4.3" />
      <path d="m10.3 10.3 3 3" />
    </>
  </Icon>
);

export const EditIcon: FunctionComponent<IconProps> = ({ size = 14, ...rest }) => (
  <Icon size={size} {...rest}>
    <path d="M11 2.6l2.4 2.4L6 12.4l-3 .6.6-3z" />
  </Icon>
);

export const TrashIcon: FunctionComponent<IconProps> = ({ size = 14, ...rest }) => (
  <Icon size={size} {...rest}>
    <path d="M3.5 5h9M6 5V3.4h4V5M5 5l.6 8h4.8L11 5" />
  </Icon>
);

export const LockIcon: FunctionComponent<IconProps> = ({ size = 13, ...rest }) => (
  <Icon size={size} {...rest}>
    <>
      <rect x="3" y="7" width="10" height="6.5" rx="1.6" />
      <path d="M5.6 7V5.2a2.4 2.4 0 0 1 4.8 0V7" />
    </>
  </Icon>
);

export const CopyIcon: FunctionComponent<IconProps> = ({ size = 13, ...rest }) => (
  <Icon size={size} {...rest}>
    <>
      <rect x="5.5" y="5.5" width="8" height="8" rx="1.5" />
      <path d="M10.5 3H3.5a.5.5 0 0 0-.5.5v7" />
    </>
  </Icon>
);

export const DragIcon: FunctionComponent<IconProps> = ({ size = 12, ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="currentColor" aria-hidden={true} {...rest}>
    <circle cx="6" cy="4" r="1.2" />
    <circle cx="10" cy="4" r="1.2" />
    <circle cx="6" cy="8" r="1.2" />
    <circle cx="10" cy="8" r="1.2" />
    <circle cx="6" cy="12" r="1.2" />
    <circle cx="10" cy="12" r="1.2" />
  </svg>
);

export const CloseIcon: FunctionComponent<IconProps> = ({ size = 13, ...rest }) => (
  <Icon size={size} {...rest}>
    <path d="M4 4l8 8M12 4l-8 8" />
  </Icon>
);

export const ChevronIcon: FunctionComponent<IconProps> = ({ size = 13, ...rest }) => (
  <Icon size={size} {...rest}>
    <path d="M6 4l4 4-4 4" />
  </Icon>
);

export const TransferIcon: FunctionComponent<IconProps> = (props) => (
  <Icon {...props}>
    <path d="M3 8h10M9.5 4.5 13 8l-3.5 3.5" />
  </Icon>
);

export const MenuIcon: FunctionComponent<IconProps> = (props) => (
  <Icon {...props}>
    <path d="M2.5 4h11M2.5 8h11M2.5 12h11" />
  </Icon>
);

export const ArrowLeftIcon: FunctionComponent<IconProps> = ({ size = 15, ...rest }) => (
  <Icon size={size} {...rest}>
    <>
      <path d="M12.5 8h-9" />
      <path d="M7 3.5 2.5 8 7 12.5" />
    </>
  </Icon>
);
